// @flow

import React, { Fragment } from 'react';

import { useMachine } from '@xstate/react';
import {
  CommunityStep,
  HelpBlock,
  Interstitial,
  Upgrade,
  Container,
  BackgroundSection,
} from '@zapier/product-get-help';

import useTrackPath from 'app/get-help/hooks/useTrackPath';
import { AutoScroll } from 'app/get-help/components/AutoScroll';
import { useFormContext } from 'app/get-help/contexts/FormContext';

import Contact from './Contact';
import Docs from './Docs';
import Submitted from './Submitted';
import helpMachine from './machine';

import type { ComponentProps } from './types';

import { emit } from '@zapier/events-toolkit-browser';
import { useTrackingContext } from '@zapier/toolbox-tracking';
import { USER_INTERACTION_EVENT } from 'app/get-help/constants';

import { Button, Spacer } from '@zapier/design-system';

const Help = ({
  actions: { unconfirm, answer, setMeta, submit },
  community,
  docs,
  pathId,
  canContinue,
  contact,
  data,
  state,
}: ComponentProps) => {
  const trackPath = useTrackPath();

  const {
    size,
    zap,
    user,
    isEmailSupportEligible,
    isPremierSupportEligible,
  } = useFormContext();

  const [
    {
      context: {
        hasDocs,
        hasCommunity,
        dotIndex,
        dotTooltips,
        currentStep: { key, title, hasFeedback },
      },
    },
    send,
  ] = useMachine(helpMachine, {
    context: {
      hasDocs: !!docs,
      hasCommunity: !!community,
      hasContact: !!contact,
      isEmailSupportEligible,
      unconfirm,
      trackPath: path => trackPath(pathId, path),
    },
  });

  /*
    Scroll to the top of the content whenever:
    * The user goes to a different step in the help flow
    * The contact form has been successfully submitted
  */
  const scrollKey = `${key} ${state === 'submitted' ? 'true' : 'false'}`;

  const { account } = useTrackingContext();

  const handleHelpCenterLinkClick = (href: string) => {
    (async () => {
      await emit(
        {
          system_name: 'support',
          interaction_name: 'contact_form_self_serve_resource_click',
          interaction_goal: 'Clicked on help resource link',
          option_selected: href,
          event_action: 'click',
          account_id: account && account.id,
          custom_tracking_fields: { help_resource: href },
        },
        USER_INTERACTION_EVENT
      );

      window.location.href = href;
    })();
  };

  const handleOnClickNoWhatsNext = () => {
    emit(
      {
        system_name: 'support',
        interaction_name: 'contact_form_this_didnt_answer_my_question',
        interaction_goal:
          'Click This didnt answer my question after the contact form suggested help',
        option_selected: 'contact_form_this_didnt_answer_my_question',
        event_action: 'click',
        account_id: account && account.id,
      },
      USER_INTERACTION_EVENT
    );
  };

  const helpStep = children => (
    <AutoScroll scrollKey={scrollKey}>
      <BackgroundSection containerPaddingBottom={40} containerPaddingTop={40}>
        <Container>
          <HelpBlock
            currentStepIndex={dotIndex}
            dotTooltips={dotTooltips}
            onClickHelpCenterLink={href => handleHelpCenterLinkClick(href)}
            onClickNoWhatsNext={handleOnClickNoWhatsNext}
            onGoBack={() => send('PREV_STEP')}
            onGoForward={() => send('NEXT_STEP')}
            onGoToStep={i => send('STEP_FROM_DOT', { dotIndex: i })}
            showFeedback={hasFeedback}
            size={size}
            title={title}
          >
            {children}
          </HelpBlock>
        </Container>
      </BackgroundSection>
    </AutoScroll>
  );

  switch (key) {
    case 'interstitial':
      return (
        <AutoScroll scrollKey={scrollKey}>
          <BackgroundSection
            containerPaddingBottom={40}
            containerPaddingTop={40}
          >
            <Container>
              <Interstitial
                hasArticles={hasDocs}
                hasCommunityConversations={hasCommunity}
                isEmailSupportEligible={isEmailSupportEligible}
                isPremierSupportEligible={isPremierSupportEligible}
                onClickBack={() => send('PREV_STEP')}
                onClickNext={() => send('NEXT_STEP')}
                onClickSkip={() => send('CONTACT')}
                size={size}
              />
            </Container>
          </BackgroundSection>
        </AutoScroll>
      );

    case 'docs':
      return helpStep(<Docs {...docs} size={size} zap={zap} />);

    case 'community':
      return helpStep(<CommunityStep links={community} size={size} />);

    case 'upgrade':
      return helpStep(
        <Upgrade
          onClickUpgrade={() => send('PREV_STEP')}
          viewPlansUrl="/pricing"
        />
      );

    case 'contact':
      if (state === 'submitted' && user && user.email) {
        return (
          <Fragment>
            <BackgroundSection
              containerPaddingBottom={40}
              containerPaddingTop={40}
            >
              <Container>
                <Submitted size={size} />
                <Spacer height={30} />
                <Button href="/app/home" size="medium">
                  Back to home
                </Button>
              </Container>
            </BackgroundSection>
          </Fragment>
        );
      }
      if (state === 'submitted') {
        return <Fragment>{helpStep(<Submitted size={size} />)}</Fragment>;
      }

      return helpStep(
        <Contact
          answer={answer}
          canContinue={canContinue}
          data={data}
          isEligible={isEmailSupportEligible}
          isSubmitting={state === 'submitting'}
          items={contact}
          setMeta={setMeta}
          submit={submit}
        />
      );

    default:
      return null;
  }
};

export default Help;
